import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import React, { memo, useCallback, useEffect, useRef } from 'react';

import CalendarIcon from 'common/shared-ui/src/icons/CalendarIcon';
import ChevronIcon from 'common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: popperRoot
const StyledPopperRoot = styled(Modal)(theme => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100vh',
  transform: 'unset',
  backgroundColor: 'rgba(31, 33, 72, 0.15)',
  zIndex: '1999 !important',
}));
// Original class: exclusionMenu
const StyledExclusionMenu = styled(Grid)(({ theme }) => ({
  width: '276px !important',
  height: 246,
  padding: theme.spacing(5),
  borderRadius: theme.spacing(4),
  backgroundColor: '#F1F2F8',
}));
// Original class: newExceptionField
const StyledNewExceptionField = styled(TextField)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(4),

  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    appearance: 'none',
    '-webkit-appearance': 'none',
  },

  '& input': {
    ...theme.typography.h5,
  },
}));
// Original class: newExceptionDatePicker
const StyledNewExceptionDatePicker = styled(DatePicker)(({ theme }) => ({
  borderRadius: theme.spacing(4),
  backgroundColor: '#ffffff',

  '& .MuiSvgIcon-root': {
    width: 16,
    height: 16,
  },

  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },

  '& input': {
    padding: '0 0 0 10px !important',
    ...theme.typography.h5,
  },
}));
const CalendarExceptionCreate = ({
  values,
  setFieldValue,
  errors,
  handleChange,
  handleToggleExclusionMenu,
  isCreateModalOpen,
  newExceptionDate,
  setNewExceptionDate,
  handleUpdateCalendars,
}) => {
  const getLocalString = useTranslation();

  const { current: handleClearNewException } = useRef(() => {
    setFieldValue('hours', 0);
  });

  const { current: handleChangeDate } = useRef((value, e) => {
    setFieldValue('date', dayjs(value).format('YYYY-MM-DD'));
    setNewExceptionDate(dayjs(value));
  });

  const handleChangeHours = useCallback(
    e => {
      const regex = /^([1]?[0-9]|2[0-4])$/;
      if (e.currentTarget.value === '') {
        return setFieldValue('hours', 0);
      }
      if (regex.test(e.currentTarget.value)) {
        return handleChange(e);
      }
    },
    [handleChange, setFieldValue],
  );

  const { current: pickerProps } = useRef({
    paperProps: { disableRipple: true },
    buttonProps: { disableRipple: true },
    renderInput: params => <TextField {...params} />,
    components: {
      OpenPickerIcon: ({ ...props }) => <CalendarIcon {...props} />,
      SwitchViewButton: (...props) => (
        <Box
          {...props}
          sx={[
            {
              display: 'flex',
              alignItems: 'center',
              width: 36,
              height: 36,
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
          ]}
        >
          <IconButton disableRipple>
            <ChevronIcon direction='down' />
            {props.children}
          </IconButton>
        </Box>
      ),
      LeftArrowButton: ({ ...props }) => (
        <Box
          sx={{
            width: 36,
            height: 36,
          }}
        >
          <IconButton disableRipple {...props}>
            <ChevronIcon direction='left' />
          </IconButton>
        </Box>
      ),
      RightArrowButton: ({ ...props }) => (
        <Box
          sx={{
            width: 36,
            height: 36,
          }}
        >
          <IconButton disableRipple {...props}>
            <ChevronIcon direction='right' />
          </IconButton>
        </Box>
      ),
    },
  });

  return (
    <StyledPopperRoot open={isCreateModalOpen} disableRestoreFocus>
      <StyledExclusionMenu
        container
        direction='column'
        wrap='nowrap'
        sx={{
          marginTop: 2,
        }}
      >
        <Grid
          container
          wrap='nowrap'
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 48,
            marginBottom: 5,
          }}
        >
          <Typography variant='h1'>
            {getLocalString('labels', 'params')}
          </Typography>
          <IconButton
            disableRipple
            onClick={handleToggleExclusionMenu}
            value='close'
          >
            <Box
              sx={{
                width: 24,
                height: 24,
              }}
            >
              <ClearIcon viewBox='0 0 24 24' />
            </Box>
          </IconButton>
        </Grid>
        <Grid container direction='column'>
          <Grid
            container
            wrap='nowrap'
            sx={{
              marginBottom: 4,
            }}
          >
            <Grid
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '16px',
              }}
            >
              <Typography variant='h5'>
                {getLocalString('labels', 'hours')}
              </Typography>
            </Grid>
            <Grid
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <StyledNewExceptionField
                variant='outlined'
                type='number'
                name='hours'
                value={values.hours}
                onChange={handleChangeHours}
                error={!!errors?.hours}
                aria-valuemax={24}
                required
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid
            container
            wrap='nowrap'
            sx={{
              alignItems: 'center',
              marginBottom: 5,
            }}
          >
            <Grid
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '16px',
              }}
            >
              <Typography variant='h5'>
                {getLocalString('labels', 'date')}
              </Typography>
            </Grid>
            <Grid
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <StyledNewExceptionDatePicker
                slots={pickerProps.components}
                name='date'
                value={dayjs(newExceptionDate)}
                onChange={handleChangeDate}
                type='date'
                renderInput={pickerProps.renderInput}
                date={dayjs(newExceptionDate, 'YYYY-MM-DD')}
                showDaysOutsideCurrentMonth={true}
                slotProps={{
                  openPickerButton: pickerProps.buttonProps,
                  popper: {
                    sx: {
                      zIndex: '1999 !important',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            wrap='nowrap'
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              disableRipple
              variant='cutGreyPrimaryBig'
              onClick={handleClearNewException}
            >
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 700,
                }}
              >
                {getLocalString('buttons', 'clear')}
              </Typography>
            </Button>
            <Box
              sx={{
                marginLeft: 0.5,
              }}
            >
              <Button
                disableRipple
                variant='cutBlueSecondary'
                onClick={handleUpdateCalendars}
              >
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {getLocalString('buttons', 'apply')}
                </Typography>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </StyledExclusionMenu>
    </StyledPopperRoot>
  );
};

export default memo(CalendarExceptionCreate);
